import { zodResolver } from '@hookform/resolvers/zod';
import { ChevronLeft, MapPin, Save } from 'lucide-react';
import { useEffect } from 'react';
import { type Resolver, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { z } from 'zod';
import { getGetVenueQueryKey, useCreateVenue, useGetVenue, useUpdateVenue } from '@/api/generated/venues';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { useToast } from '@/hooks/use-toast';

const venueSchema = z.object({
  code: z.string().min(1, '会場コードは必須です'),
  name: z.string().min(1, '会場名は必須です'),
  address: z.string().optional(),
  mapUrl: z.string().optional(),
  capacity: z.coerce.number().int().optional(),
  isActive: z.boolean().default(true),
});

type VenueFormValues = z.infer<typeof venueSchema>;

export default function VenueForm() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const { toast } = useToast();

  const isNew = id === 'new';

  const { data: result, isLoading: isLoadingData } = useGetVenue(id || '', {
    query: {
      enabled: !isNew && !!id,
      queryKey: getGetVenueQueryKey(id || ''),
    },
  });

  const createMutation = useCreateVenue();
  const updateMutation = useUpdateVenue();

  const form = useForm<VenueFormValues>({
    resolver: zodResolver(venueSchema) as unknown as Resolver<VenueFormValues>,
    defaultValues: {
      code: '',
      name: '',
      address: '',
      mapUrl: '',
      capacity: undefined,
      isActive: true,
    },
  });

  useEffect(() => {
    if (result?.data && result.status === 200) {
      const data = result.data;
      form.reset({
        code: data.code,
        name: data.name,
        address: data.address || '',
        mapUrl: data.mapUrl || '',
        capacity: data.capacity ?? undefined,
        isActive: data.isActive,
      });
    }
  }, [result, form]);

  const onSubmit = async (values: VenueFormValues) => {
    try {
      if (isNew) {
        await createMutation.mutateAsync({
          data: {
            code: values.code,
            name: values.name,
            address: values.address || undefined,
            mapUrl: values.mapUrl || undefined,
            capacity: values.capacity || undefined,
          },
        });
      } else {
        await updateMutation.mutateAsync({
          id: id || '',
          data: {
            code: values.code,
            name: values.name,
            address: values.address || undefined,
            mapUrl: values.mapUrl || undefined,
            capacity: values.capacity || undefined,
            isActive: values.isActive,
          },
        });
      }

      toast({
        title: isNew ? '会場を作成しました' : t('modules.venues.updated'),
        description: values.name,
      });
      navigate('/admin/master/venues');
    } catch (_error) {
      toast({
        title: 'エラーが発生しました',
        description: t('modules.venues.saveFailed'),
        variant: 'destructive',
      });
    }
  };

  const loading = isLoadingData || createMutation.isPending || updateMutation.isPending;

  return (
    <div className="p-8 max-w-4xl mx-auto space-y-8">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/admin/master/venues')}>
          <ChevronLeft className="w-4 h-4" />
        </Button>
        <h1 className="text-3xl font-bold tracking-tight text-gray-900">
          {isNew ? t('modules.venues.createTitle') : t('modules.venues.editTitle')}
        </h1>
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
          <Card className="border-t-4 border-t-emerald-500 shadow-lg">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-emerald-700">
                <MapPin className="w-5 h-5" />
                {t('modules.venues.basicInfo')}
              </CardTitle>
              <CardDescription>{t('modules.venues.description')}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <FormField
                  control={form.control}
                  name="code"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('modules.venues.code')}</FormLabel>
                      <FormControl>
                        <Input placeholder="V001" {...field} />
                      </FormControl>
                      <FormDescription>一意の識別コードを入力してください</FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('modules.venues.name')}</FormLabel>
                      <FormControl>
                        <Input placeholder="本学会場" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="address"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('modules.venues.address')}</FormLabel>
                    <FormControl>
                      <Input placeholder="東京都..." {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <FormField
                  control={form.control}
                  name="capacity"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('modules.venues.capacity')}</FormLabel>
                      <FormControl>
                        <Input type="number" placeholder="100" {...field} />
                      </FormControl>
                      <FormDescription>この会場の最大収容人数を入力します</FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="isActive"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                      <div className="space-y-0.5">
                        <FormLabel>{t('modules.venues.status')}</FormLabel>
                        <FormDescription>無効にすると入試日程の設定で選択できなくなります</FormDescription>
                      </div>
                      <FormControl>
                        <Switch checked={!!field.value} onCheckedChange={field.onChange} />
                      </FormControl>
                    </FormItem>
                  )}
                />
              </div>
            </CardContent>
          </Card>

          <div className="flex justify-end gap-4">
            <Button variant="outline" type="button" onClick={() => navigate('/admin/master/venues')}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" className="bg-emerald-600 hover:bg-emerald-700" disabled={loading}>
              <Save className="w-4 h-4 mr-2" />
              {loading ? t('common.loading') : t('common.save')}
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
}
